// src/pages/supplier/ProductsPage.jsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Pencil, Trash2, SendHorizontal } from 'lucide-react'
import { supplierProductsApi } from '../../api/services'
import { Button, Card, CardBody, StatusBadge, PageSpinner, Empty, Pagination } from '../../components/common/ui'
import toast from 'react-hot-toast'

const TABS = [{ key:'', label:'All' },{ key:'draft', label:'Draft' },{ key:'pending_review', label:'Pending' },{ key:'published', label:'Live' },{ key:'rejected', label:'Rejected' }]

export default function ProductsPage() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['supplier-products', status, search, page], queryFn: () => supplierProductsApi.list({ status: status || undefined, search: search || undefined, page }) })
  const del = useMutation({ mutationFn: id => supplierProductsApi.delete(id), onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['supplier-products']) } })
  const submit = useMutation({ mutationFn: id => supplierProductsApi.submitReview(id), onSuccess: () => { toast.success('Submitted for review!'); qc.invalidateQueries(['supplier-products']) }, onError: e => toast.error(e.response?.data?.message || 'Error') })

  const products = data?.data?.data?.products?.data || []
  const counts = data?.data?.data?.counts || {}
  const lastPage = data?.data?.data?.products?.last_page || 1

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">My Products</h1>
        <Link to="/supplier/products/new"><Button size="sm"><Plus size={14} /> Add Product</Button></Link>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setStatus(t.key); setPage(1) }}
            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${status === t.key ? 'bg-pink text-white border-pink' : 'border-border text-muted hover:border-pink'}`}>
            {t.label} {counts[t.key || 'all'] != null ? `(${counts[t.key || 'all']})` : ''}
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input className="input pl-9 w-full" placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {isLoading && <PageSpinner />}
      {!isLoading && products.length === 0 && <Empty icon="🧵" title="No products" description="Add your first fabric product." action={<Link to="/supplier/products/new"><Button>Add Product</Button></Link>} />}

      <div className="space-y-3">
        {products.map(p => (
          <Card key={p.id}>
            <CardBody className="flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-lg bg-surface border border-border overflow-hidden flex-shrink-0">
                {p.primary_image?.url && <img src={p.primary_image.url} alt={p.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{p.name}</span>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-xs text-muted">{p.category?.name} · {p.variants_count ?? 0} variant(s)</p>
                <p className="text-sm font-bold text-pink mt-0.5">₹{Number(p.retail_price).toLocaleString('en-IN')}/m</p>
              </div>
              <div className="flex gap-2">
                {p.status === 'draft' && (
                  <Button size="sm" variant="secondary" loading={submit.isPending} onClick={() => submit.mutate(p.id)}>
                    <SendHorizontal size={13} /> Submit
                  </Button>
                )}
                <Link to={`/supplier/products/${p.id}/edit`}><Button size="sm" variant="secondary"><Pencil size={13} /></Button></Link>
                <Button size="sm" variant="danger" onClick={() => del.mutate(p.id)}><Trash2 size={13} /></Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
      {lastPage > 1 && <div className="mt-6"><Pagination currentPage={page} lastPage={lastPage} onPageChange={setPage} /></div>}
    </div>
  )
}
